import { limitMoneyInput } from '../../../utils/formatters';
import { centsToDollarsInput } from './money';
import { coerceOperatingHours } from '../operatingHours';
import type { AddressForm } from './types';

export const createEmptyForm = (): AddressForm => ({
  label: '',
  fullName: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'USA',
  carrierRates: {},
  orgLoadOptionIds: [],
  defaultFulfillmentMethodId: '',
  customerUsers: [],
  isPrimary: false,
  isHeadquarters: false,
  operatingHours: null,
  deliveryNotes: '',
  requiresPoNumber: false,
});

/**
 * A saved address back into form state.
 *
 * The two `??` chains are not defensive padding: `loadOptions` comes back with
 * the join row's own id alongside the `orgLoadOptionId` it points at, and the
 * form stores the latter — reading the wrong one silently deselects every
 * method. `defaultFulfillmentMethod` may arrive as an id or as a relation.
 */
export const buildFormFromAddress = (row: any): AddressForm => {
  const carrierRates = Object.fromEntries(
    (row?.carrierRates ?? []).map((rate: any) => [
      rate.warehouseId,
      centsToDollarsInput(rate.carrierRateCents),
    ])
  );

  const orgLoadOptionIds = (row?.loadOptions ?? [])
    .map((option: any) => option.orgLoadOptionId ?? option.id)
    .filter(Boolean);

  return {
    label: row?.label ?? '',
    fullName: row?.fullName ?? '',
    phone: row?.phone ?? '',
    line1: row?.line1 ?? '',
    line2: row?.line2 ?? '',
    city: row?.city ?? '',
    state: row?.state ?? '',
    postalCode: row?.postalCode ?? '',
    country: row?.country ?? 'USA',
    carrierRates,
    orgLoadOptionIds,
    defaultFulfillmentMethodId:
      row?.defaultFulfillmentMethodId ?? row?.defaultFulfillmentMethod?.id ?? '',
    customerUsers: row?.customerUsers ?? [],
    isPrimary: Boolean(row?.isPrimary),
    isHeadquarters: Boolean(row?.isHeadquarters),
    operatingHours: coerceOperatingHours(row?.operatingHours),
    deliveryNotes: row?.deliveryNotes ?? '',
    requiresPoNumber: Boolean(row?.requiresPoNumber),
  };
};

/**
 * Assign or unassign a fulfilment method.
 *
 * The default follows the selection: unassigning the current default promotes
 * the first remaining method rather than leaving a default that is no longer
 * assigned — which the server rejects with DEFAULT_LOAD_OPTION_NOT_ASSIGNED.
 */
export const toggleLoadOption = (
  form: AddressForm,
  loadOptionId: string,
  checked: boolean
): AddressForm => {
  const existing = form.orgLoadOptionIds ?? [];

  const nextIds = checked
    ? Array.from(new Set([...existing, loadOptionId]))
    : existing.filter((id) => id !== loadOptionId);

  let nextDefaultId = form.defaultFulfillmentMethodId;

  if (!nextIds.includes(nextDefaultId)) {
    nextDefaultId = nextIds[0] ?? '';
  }

  return { ...form, orgLoadOptionIds: nextIds, defaultFulfillmentMethodId: nextDefaultId };
};

export const updateCarrierRate = (
  form: AddressForm,
  warehouseId: string,
  value: string
): AddressForm => ({
  ...form,
  carrierRates: { ...form.carrierRates, [warehouseId]: limitMoneyInput(value) },
});

/**
 * Removes the key entirely rather than setting it to "". The save path filters
 * blanks out anyway, but an absent key is what "this warehouse has no rate"
 * means everywhere else in this module.
 */
export const clearCarrierRate = (form: AddressForm, warehouseId: string): AddressForm => {
  const nextCarrierRates = { ...form.carrierRates };
  delete nextCarrierRates[warehouseId];
  return { ...form, carrierRates: nextCarrierRates };
};

/** How many warehouses have a rate typed in, for the form's own counter. */
export const countConfiguredRates = (form: AddressForm): number =>
  Object.values(form.carrierRates ?? {}).filter((value) => String(value).trim() !== '').length;

/** How many rates a saved address has, preferring the server's own count. */
export const getConfiguredRateCount = (address: any): number => {
  if (Number.isFinite(Number(address?.configuredCarrierRateCount))) {
    return Number(address.configuredCarrierRateCount);
  }

  return Array.isArray(address?.carrierRates) ? address.carrierRates.length : 0;
};

export const getCustomerUserDisplayName = (customerUser: any): string =>
  customerUser?.name?.trim() || customerUser?.email || 'Unnamed user';
